import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import ProducrGradeModel from './ProducrGradeModel';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import type {
    ProductGrades,
} from '../../../../types/CreateProductType';
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));


const ProductGradesindex: React.FC = () => {
  const [productgrades, setProductGrades] = useState<ProductGrades[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<ProductGrades | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchProductGrade = async () => {
    try {
      const data = await apiRequest<ProductGrades[]>({
        method: 'get',
        url: 'product-grades',
      });
      setProductGrades(data);
    } catch (error) {
      showError('Failed to fetch productgrades');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchProductGrade();
  }, []);

  const handleEditClick = (record: ProductGrades) => {
    setIsEditMode(true);
    setInitialValues({
      pg_id: record.pg_id,
      pg_name: record.pg_name,
      pg_status: record.pg_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchProductGrade();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (pg_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-product-grade/${pg_id}`,
      });
      showSuccess('product-grade deleted');
      fetchProductGrade();
    } catch {
      showError('Failed to delete product-grade');
    }
  };

  const handleStatusToggle = async (PGId: number) => {
    setProductGrades((prev) =>
      prev.map((item) =>
        item.pg_id === PGId
          ? { ...item, pg_status: item.pg_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-product-grade/${PGId}`,
      });
      showSuccess('Product grade status updated');
    } catch (error) {
      showError('Failed to update status');
      setProductGrades((prev) =>
        prev.map((item) =>
          item.pg_id === PGId
            ? { ...item, pg_status: item.pg_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<ProductGrades>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<ProductGrades> = [
    {
      title: 'Product Grade',
      dataIndex: 'pg_name',
      key: 'pg_name',
      filters: Array.from(new Set(productgrades.map((s) => s.pg_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.pg_name || null,
      onFilter: (value, record) => record.pg_name.includes(value as string),
      sorter: (a, b) => a.pg_name.localeCompare(b.pg_name),
      sortOrder: sortedInfo.columnKey === 'pg_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.pg_name}</div>,
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: ProductGrades) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.pg_status}
                onChange={() => handleStatusToggle(record.pg_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this product grade?"
              onConfirm={() => handleDelete(record.pg_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View ProductGrades</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Product Grade
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<ProductGrades>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="pg_id"
          dataSource={productgrades.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 14 }}
          locale={{ emptyText: 'No Product Grade found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, productgrades.length)} of{' '}
                {productgrades.length}
              </span>
              <Pagination
                {...pagination}
                total={productgrades.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <ProducrGradeModel
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default ProductGradesindex;